import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Autocomplete,
  Grid,
  Button,
  Typography,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { BASE_URL, DISABLED_MODULE } from "./../../global";
import { ToastContainer, toast } from "react-toastify";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import AddIcon from "@mui/icons-material/Add";
import { multiValueModalStyle } from "../../constants/srfConstants";

const MultiValueInputModal = ({
  buttonLabel,
  transformedStringLabel,
  inputStates,
  updateInputObject,
  disciplineId,
}) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([
    { min: "", max: "", minUnit: "", maxUnit: "" },
  ]);
  const [unitArray, setUnitArray] = React.useState([]);
  const [unitFilter, setUnitFilter] = React.useState([]);

  const handleOpen = () => {
    setOpen(true);
    const groups = inputStates?.[transformedStringLabel]?.split("||");
    const result = groups?.map((group) => {
      const parts = group?.split("|");
      let min, max, minUnit, maxUnit; // ✅ changed: replaced 'unit' with 'minUnit' and 'maxUnit'

      if (parts.length === 1) {
        // Example: "10#bar"
        min = parts[0]?.split("#")[0];
        minUnit = parts[0]?.split("#")[1];
        max = "";
        maxUnit = "";
      } else if (parts.length === 2) {
        // Example: "10#bar|20#psi"
        min = parts[0]?.split("#")[0];
        minUnit = parts[0]?.split("#")[1];
        max = parts[1]?.split("#")[0];
        maxUnit = parts[1]?.split("#")[1];
      }

      return {
        min,
        max,
        minUnit,
        maxUnit, // ✅ changed: added new fields
      };
    });

    inputStates?.[transformedStringLabel] && setRows(result);
  };

const handleClose = () => {
  const formattedValue = rows
    .filter((row) => row.min || row.max) 
    .map((row) => {
      const minPart = row.min ? `${row.min}${row.minUnit ? `#${row.minUnit}` : ""}` : "";
      const maxPart = row.max ? `|${row.max}${row.maxUnit ? `#${row.maxUnit}` : ""}` : "";
      return `${minPart}${maxPart}`;
    })
    .filter((val) => val.trim() !== "") 
    .join("||");

  updateInputObject(transformedStringLabel, formattedValue); 
  setOpen(false);
};


  const handleAddRow = () => {
    const newRow = {
      min: "",
      max: "",
      minUnit: rows[0]?.minUnit || "",
      maxUnit: rows[0]?.maxUnit || "",
    };
    setRows([...rows, newRow]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const getUnitMasterArray = () => {
    let url = BASE_URL;
    axiosWithToken
      .get(
        url +
          `unit?_where=(status,eq,1)${
            disciplineId ? `~and(disciplineId,eq,${disciplineId})` : ""
          }`
      )
      .then((res) => {
        setUnitArray([""].concat(res.data));
      })
      .catch((error) => {
        if (error.message !== "request_aborted") {
          toast.error("Something Went Wrong!");
        }
      });
  };

  const UnitList = () => {
    var uArr = [];
    for (let i = 0; i < unitArray.length; i++) {
      uArr.push({
        label: unitArray[i].symbol,
      });
    }
    setUnitFilter(uArr);
  };

  useEffect(() => {
    getUnitMasterArray();
  }, [open,disciplineId]);

    useEffect(() => {
    UnitList();
  }, [unitArray]);

  return (
    <div>
      <Button
        id={`ducMaster_create_${transformedStringLabel}`}
        variant="outlined"
        size="small"
        onClick={handleOpen}
         sx={{
          backgroundColor: inputStates?.[transformedStringLabel] ? "#a5d6a7" : "transparent", // light green if value present
          "&:hover": {
            backgroundColor: inputStates?.[transformedStringLabel]
              ? "#a5d6a7"
              : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        {buttonLabel}
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={multiValueModalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "5px",
              padding: "10px",
            }}
          >
            <Typography variant="body1" color="initial">
              Update Multiple values
            </Typography>
            <Button variant="contained" onClick={handleClose}>
              X
            </Button>
          </Box>
          <table
            className="srf-multivalue-table"
            style={{
              width: "95%",
              margin: "auto",
              borderCollapse: "collapse",
              marginBottom: "15px",
            }}
          >
            <thead>
              <tr>
                {["lc", "accuracy","masteraccuracy","masterleastcount"].includes(transformedStringLabel) ? (
                  <th
                    colSpan={2}
                    style={{ border: "none", padding: "6px 0px" }}
                  >
                   {`${buttonLabel}`}

                  </th>
                ) : (
                  <>
                    <th
                      colSpan={2}
                      style={{ border: "none", padding: "6px 0px" }}
                    >
                       {`Lower ${buttonLabel}`}
                    </th>
                    <th
                      colSpan={3}
                      style={{ border: "none", padding: "6px 0px" }}
                    >
                      {`Higher ${buttonLabel}`}
                    </th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {rows?.map((row, index) =>{ 
                return(
                <tr key={index} style={{ border: "none", padding: "6px" }}>
                  {/* Always show Min Value and Min Unit */}
                  <td style={{ border: "none", padding: "6px" }}>
                    <input
                      type="text"
                      className="form-control no-border"
                      value={row.min}
                      onChange={(e) =>
                        handleInputChange(index, "min", e.target.value)
                      }
                      style={{ width: "100%", padding: "6px" }}
                    />
                  </td>

                  <td style={{ border: "none", padding: "6px" }}>
                    <select
                      className="form-select no-border"
                      value={row.minUnit}
                      onChange={(e) =>
                        handleInputChange(index, "minUnit", e.target.value)
                      }
                      style={{ width: "100%", padding: "6px" }}
                    >
                      <option value="">Select Unit</option>
                      {unitFilter?.map((option) => (
                        <option key={option.label} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Conditionally show Max Value and Max Unit */}
                  {!["lc", "accuracy","masteraccuracy","masterleastcount"].includes(transformedStringLabel) && (
                    <>
                      <td style={{ border: "none", padding: "6px" }}>
                        <input
                          type="text"
                          className="form-control no-border"
                          value={row.max}
                          onChange={(e) =>
                            handleInputChange(index, "max", e.target.value)
                          }
                          style={{ width: "100%", padding: "6px" }}
                        />
                      </td>

                      <td style={{ border: "none", padding: "6px" }}>
                        <select
                          className="form-select no-border"
                          value={row.maxUnit}
                          onChange={(e) =>
                            handleInputChange(index, "maxUnit", e.target.value)
                          }
                          style={{ width: "100%", padding: "6px" }}
                        >
                          <option value="">Select Unit</option>
                          {unitFilter?.map((option) => (
                            <option key={option.label} value={option.label}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </>
                  )}

                  {/* Delete Icon */}
                  <td style={{ border: "none", textAlign: "center" }}>
                    <DeleteIcon
                      onClick={() => handleDeleteRow(index)}
                      style={{
                        cursor: "pointer",
                        color: "red",
                        fontSize: "22px",
                      }}
                    />
                  </td>
                </tr>
)})}

              {/* Add Row Button */}
              <tr>
                <td
                  colSpan="5"
                  style={{
                    textAlign: "center",
                    border: "none",
                    padding: "10px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      setRows([
                        ...rows,
                        { min: "", max: "", minUnit: "", maxUnit: "" },
                      ])
                    }
                  >
                    <b style={{ fontSize: "16px" }}>ADD</b>
                    <AddIcon />
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Modal>
    </div>
  );
};

export default MultiValueInputModal;
